import React, { useState } from 'react';
import Icon from 'components/AppIcon';

function SectionCard({ title, description, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
      <div className="mb-5">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
      </div>
      <div className="border-t border-slate-100 pt-5">{children}</div>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 mb-5">
      <div className="sm:w-48 flex-shrink-0">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default function WorkspaceSection({ isAdmin, showToast }) {
  const [workspaceName, setWorkspaceName] = useState('SeedDrive Demo');
  const [currency, setCurrency] = useState('GBP');
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [timezone, setTimezone] = useState('Europe/London');
  const [fiscalYear, setFiscalYear] = useState('January');
  const [accentColor, setAccentColor] = useState('#3b4c4c');
  const [tagline, setTagline] = useState('Infrastructure equity, structured for institutions');
  const [reportHeader, setReportHeader] = useState('SeedDrive Infrastructure Equity Platform\nConfidential — For Authorised Recipients Only\nPrepared by SeedDrive Advisory Services');
  const [retention, setRetention] = useState('7 years');
  const [anonymise, setAnonymise] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const inputCls = `w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30 focus:border-[#3b4c4c] transition-colors ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`;
  const selectCls = `w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30 focus:border-[#3b4c4c] transition-colors ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`;

  return (
    <div>
      {/* General */}
      <SectionCard title="General" description="Core workspace configuration and regional settings.">
        <FieldRow label="Workspace Name" hint="Visible to all members">
          <div className="relative">
            <input
              type="text"
              value={workspaceName}
              onChange={e => isAdmin && setWorkspaceName(e?.target?.value)}
              readOnly={!isAdmin}
              className={inputCls}
            />
            {!isAdmin && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2" title="Admin access required">
                <Icon name="Lock" size={14} color="#94a3b8" />
              </span>
            )}
          </div>
        </FieldRow>

        <FieldRow label="Workspace Logo" hint="PNG or SVG, max 2MB">
          <div
            onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e?.preventDefault(); setDragOver(false); if (isAdmin) showToast('Logo updated'); }}
            className={`flex items-center gap-4 p-4 border-2 border-dashed rounded-xl transition-colors ${
              dragOver ? 'border-[#3b4c4c] bg-[#3b4c4c]/5' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#3b4c4c] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">SD</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">Drag & drop your logo here, or</p>
              <button
                onClick={() => isAdmin && showToast('Logo updated')}
                disabled={!isAdmin}
                className="mt-1 text-xs font-semibold text-[#3b4c4c] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Change Logo
              </button>
            </div>
          </div>
        </FieldRow>

        <FieldRow label="Primary Currency">
          <select value={currency} onChange={e => isAdmin && setCurrency(e?.target?.value)} disabled={!isAdmin} className={selectCls}>
            <option value="GBP">GBP (£) — British Pound</option>
            <option value="USD">USD ($) — US Dollar</option>
            <option value="EUR">EUR (€) — Euro</option>
          </select>
        </FieldRow>

        <FieldRow label="Date Format">
          <select value={dateFormat} onChange={e => isAdmin && setDateFormat(e?.target?.value)} disabled={!isAdmin} className={selectCls}>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </FieldRow>

        <FieldRow label="Timezone">
          <select value={timezone} onChange={e => isAdmin && setTimezone(e?.target?.value)} disabled={!isAdmin} className={selectCls}>
            <option value="Europe/London">Europe/London (GMT+0)</option>
            <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
            <option value="America/New_York">America/New_York (GMT-5)</option>
            <option value="America/Los_Angeles">America/Los_Angeles (GMT-8)</option>
            <option value="Asia/Singapore">Asia/Singapore (GMT+8)</option>
          </select>
        </FieldRow>

        <FieldRow label="Language">
          <select disabled className={`${selectCls} opacity-60 cursor-not-allowed`}>
            <option>English (UK)</option>
            <option disabled>French — Coming soon</option>
            <option disabled>German — Coming soon</option>
          </select>
        </FieldRow>

        <FieldRow label="Fiscal Year Start">
          <select value={fiscalYear} onChange={e => isAdmin && setFiscalYear(e?.target?.value)} disabled={!isAdmin} className={selectCls}>
            {['January','February','March','April','May','June','July','August','September','October','November','December']?.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </FieldRow>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => isAdmin && showToast('Workspace settings saved')}
            disabled={!isAdmin}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <Icon name="Save" size={15} />
            Save Changes
          </button>
        </div>
      </SectionCard>
      {/* Branding */}
      <SectionCard title="Branding" description="Customise the visual identity of your workspace.">
        <FieldRow label="Accent Colour" hint="Used in reports and UI accents">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={accentColor}
              onChange={e => isAdmin && setAccentColor(e?.target?.value)}
              disabled={!isAdmin}
              className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer disabled:cursor-not-allowed p-0.5"
            />
            <input
              type="text"
              value={accentColor}
              onChange={e => isAdmin && setAccentColor(e?.target?.value)}
              readOnly={!isAdmin}
              className="w-36 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30"
            />
          </div>
        </FieldRow>

        <FieldRow label="Company Tagline">
          <input
            type="text"
            value={tagline}
            onChange={e => isAdmin && setTagline(e?.target?.value)}
            readOnly={!isAdmin}
            className={inputCls}
          />
        </FieldRow>

        <FieldRow label="Default Report Header" hint="Appears on report cover pages">
          <textarea
            value={reportHeader}
            onChange={e => isAdmin && setReportHeader(e?.target?.value)}
            readOnly={!isAdmin}
            rows={4}
            className={`${inputCls} resize-none`}
          />
        </FieldRow>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => isAdmin && showToast('Branding settings saved')}
            disabled={!isAdmin}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <Icon name="Save" size={15} />
            Save Changes
          </button>
        </div>
      </SectionCard>
      {/* Data & Privacy */}
      <SectionCard title="Data & Privacy" description="Manage data retention, exports, and workspace lifecycle.">
        <FieldRow label="Data Retention Policy">
          <select value={retention} onChange={e => isAdmin && setRetention(e?.target?.value)} disabled={!isAdmin} className={selectCls}>
            <option value="1 year">1 year</option>
            <option value="3 years">3 years</option>
            <option value="7 years">7 years</option>
            <option value="Indefinite">Indefinite</option>
          </select>
        </FieldRow>

        <FieldRow label="Anonymise Inactive Users" hint="Auto-anonymise users inactive for 12+ months">
          <button
            onClick={() => isAdmin && setAnonymise(!anonymise)}
            disabled={!isAdmin}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
              anonymise ? 'bg-[#3b4c4c]' : 'bg-slate-200'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              anonymise ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </FieldRow>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={() => isAdmin && showToast('Data export requested — you will receive an email when ready')}
            disabled={!isAdmin}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="Download" size={15} />
            Export All Workspace Data
          </button>
          <button
            onClick={() => isAdmin && setShowDeleteModal(true)}
            disabled={!isAdmin}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="Trash2" size={15} />
            Delete Workspace
          </button>
        </div>
      </SectionCard>
      {/* Delete Workspace Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} color="#dc2626" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Delete Workspace</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">Type <strong>DELETE</strong> to confirm workspace deletion. All data will be permanently removed.</p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e?.target?.value)}
              placeholder="Type DELETE to confirm"
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); }} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm === 'DELETE') {
                    setShowDeleteModal(false);
                    setDeleteConfirm('');
                    showToast('Workspace deletion requested — this action cannot be undone');
                  }
                }}
                disabled={deleteConfirm !== 'DELETE'}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Delete Workspace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
